import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import GoogleAuthButton from '../components/GoogleAuthButton';
import SiteLinks from '../components/SiteLinks';
import EmailVerificationCard from '../components/EmailVerificationCard';
import AuthBrand from '../components/AuthBrand';
import { APP_HOME, IS_MESSENGER } from '../config/appVariant';

const ORGANIZATION_CATEGORIES = [
  { value: '', label: 'Select category' },
  { value: 'brand', label: 'Brand / Company' },
  { value: 'nonprofit', label: 'Nonprofit / NGO' },
  { value: 'media', label: 'Media' },
  { value: 'government', label: 'Government' },
  { value: 'education', label: 'Education' },
  { value: 'public_figure', label: 'Public figure' },
  { value: 'other', label: 'Other' }
];

const Register = () => {
  const { t } = useTranslation();
  const [accountType, setAccountType] = useState('individual');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    handle: '',
    organizationDisplayName: '',
    organizationCategory: '',
    website: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSuccess = () => {
    navigate(APP_HOME);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (accountType === 'organization' && !formData.organizationDisplayName?.trim()) {
      setError('Organization display name is required');
      return;
    }
    setLoading(true);

    const result = await register(
      formData.username,
      formData.email,
      formData.password,
      formData.handle,
      {
        accountType,
        acceptTerms,
        organizationDisplayName: accountType === 'organization' ? formData.organizationDisplayName?.trim() : undefined,
        organizationCategory: accountType === 'organization' ? formData.organizationCategory || undefined : undefined,
        website: formData.website?.trim() || undefined
      }
    );
    
    if (result.success) {
      if (result.requireVerification) {
        setVerificationEmail(result.email || formData.email);
        setVerificationMessage(result.message || '');
        setVerificationRequired(true);
      } else {
        navigate(APP_HOME);
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (verificationRequired) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-black py-10 px-4 sm:px-6">
        <div className="max-w-sm w-full space-y-5 bg-white dark:bg-zinc-900 p-6 sm:p-7 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm">
          <EmailVerificationCard
            email={verificationEmail}
            initialMessage={verificationMessage}
            onSuccess={() => navigate(APP_HOME)}
            onCancel={() => setVerificationRequired(false)}
          />
        </div>
        <SiteLinks className="mt-5 max-w-sm justify-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-black py-10 px-4 sm:px-6">
      <div className="max-w-sm w-full space-y-5 bg-white dark:bg-zinc-900 p-6 sm:p-7 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm">
        <div>
          <AuthBrand />
          <h2 className="text-center text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('auth.register', 'Create your account')}
          </h2>
          <p className="text-center text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
            {IS_MESSENGER ? 'A Bazzef account is all you need to message.' : 'Join the conversation today.'}
          </p>

          {/* Social Registration */}
          <div className="mt-4">
            <GoogleAuthButton
              mode="register"
              onSuccess={handleGoogleSuccess}
              onError={(msg) => setError(msg)}
            />

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-200 dark:border-zinc-800" />
              </div>
              <div className="relative flex justify-center text-[11px] uppercase tracking-wider font-medium">
                <span className="bg-white dark:bg-zinc-900 px-2.5 text-zinc-400 dark:text-zinc-500">
                  {t('auth.orContinueWithEmail', 'or continue with email')}
                </span>
              </div>
            </div>
          </div>

          {/* Account type */}
          <div className="flex gap-2.5 justify-center mt-3">
            <button
              type="button"
              onClick={() => setAccountType('individual')}
              className={`flex-1 py-2 px-3 rounded-xl border text-xs font-semibold transition-all flex items-center justify-center gap-2 ${
                accountType === 'individual'
                  ? 'border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 shadow-xs'
                  : 'border-zinc-200 dark:border-zinc-800 text-zinc-500 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700 bg-white dark:bg-zinc-900'
              }`}
            >
              <i className="fas fa-user text-xs" />
              Personal
            </button>
            <button
              type="button"
              onClick={() => setAccountType('organization')}
              className={`flex-1 py-2 px-3 rounded-xl border text-xs font-semibold transition-all flex items-center justify-center gap-2 ${
                accountType === 'organization'
                  ? 'border-zinc-900 dark:border-zinc-100 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 shadow-xs'
                  : 'border-zinc-200 dark:border-zinc-800 text-zinc-500 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700 bg-white dark:bg-zinc-900'
              }`}
            >
              <i className="fas fa-building text-xs" />
              Organization
            </button>
          </div>
        </div>

        <form className="space-y-3" onSubmit={handleSubmit}>
          {error && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 text-red-600 dark:text-red-400 px-3.5 py-2.5 rounded-xl text-xs font-medium">
              <i className="fas fa-circle-exclamation text-sm shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2.5">
            <div className="relative">
              <i className="far fa-user absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
              <input
                type="text"
                name="username"
                required
                value={formData.username}
                onChange={handleChange}
                className="appearance-none relative block w-full pl-10 pr-3.5 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                placeholder="Full Name"
              />
            </div>

            <div className="relative">
              <i className="fas fa-at absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
              <input
                type="text"
                name="handle"
                required
                value={formData.handle}
                onChange={handleChange}
                className="appearance-none relative block w-full pl-10 pr-3.5 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                placeholder="handle (e.g. jsmith)"
              />
            </div>

            <div className="relative">
              <i className="far fa-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="appearance-none relative block w-full pl-10 pr-3.5 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                placeholder="Email address"
              />
            </div>

            <div className="relative">
              <i className="fas fa-lock absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none relative block w-full pl-10 pr-10 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm focus:outline-none p-1 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showPassword ? 'far fa-eye-slash' : 'far fa-eye'} />
              </button>
            </div>

            {accountType === 'organization' && (
              <div className="space-y-2.5 pt-1 border-t border-zinc-100 dark:border-zinc-800">
                <div className="relative">
                  <i className="far fa-id-badge absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
                  <input
                    type="text"
                    name="organizationDisplayName"
                    required
                    value={formData.organizationDisplayName}
                    onChange={handleChange}
                    className="appearance-none relative block w-full pl-10 pr-3.5 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                    placeholder="Organization / legal name"
                  />
                </div>
                <div className="relative">
                  <i className="fas fa-shapes absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
                  <select
                    name="organizationCategory"
                    value={formData.organizationCategory}
                    onChange={handleChange}
                    className="appearance-none relative block w-full pl-10 pr-8 py-2.5 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all cursor-pointer"
                  >
                    {ORGANIZATION_CATEGORIES.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value} className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs pointer-events-none" />
                </div>
                <div className="relative">
                  <i className="fas fa-globe absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none" />
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="appearance-none relative block w-full pl-10 pr-3.5 py-2.5 border border-zinc-200 dark:border-zinc-800 placeholder-zinc-400 dark:placeholder-zinc-500 text-zinc-900 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-white dark:bg-zinc-900 transition-all shadow-xs"
                    placeholder="Website (optional)"
                  />
                </div>
              </div>
            )}
          </div>

          <label className="flex items-start gap-2.5 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
            <input
              type="checkbox"
              required
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-0.5 rounded border-zinc-300 dark:border-zinc-700"
            />
            <span>
              {t('account.agreePrefix', 'I agree to the')}{' '}
              <Link to="/terms" target="_blank" rel="noopener" className="font-semibold">
                {t('site.termsTitle', 'Terms of Use')}
              </Link>{' '}
              {t('account.and', 'and')}{' '}
              <Link to="/privacy" target="_blank" rel="noopener" className="font-semibold">
                {t('site.privacyTitle', 'Privacy Policy')}
              </Link>
              .
            </span>
          </label>

          <div className="pt-1">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-xl text-white dark:text-zinc-900 bg-zinc-900 dark:bg-zinc-100 hover:bg-zinc-800 dark:hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-900 dark:focus:ring-zinc-100 disabled:opacity-50 transition-all shadow-sm active:scale-[0.99]"
            >
              {loading ? 'Creating account...' : 'Sign up'}
            </button>
          </div>

          <div className="text-center pt-1">
            <Link
              to="/login"
              className="text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              Already have an account? <span className="font-semibold text-sky-500 hover:text-sky-600">Sign in</span>
            </Link>
          </div>
        </form>
      </div>
      <SiteLinks className="mt-5 max-w-sm justify-center" />
    </div>
  );
};

export default Register;
